/**
 * Catálogo de permisos con metadata de presentación.
 *
 * Única fuente de verdad para la UI de administración de roles: cada permiso
 * declara su grupo (tab), sección (subheader), etiqueta, descripción y la
 * columna donde se renderiza su checkbox. Agregar un permiso nuevo = una
 * entrada acá; la UI se genera sola.
 *
 * Las entradas que comparten grupo + sección + label se renderizan como UNA
 * fila con un checkbox por columna (p.ej. el CRUD de "Usuarios").
 *
 * Nota: CountriesPermission y StateTransitionPermission no se exponen acá.
 * La primera nunca estuvo en la UI de roles; la segunda se administra en la
 * matriz de estados del workflow (statePermissions), que es un control aparte.
 */
import {
  AvailabilityPermission,
  ClientAppPermission,
  ClientConfigPermission,
  ClientsPermission,
  CreditsPermission,
  DeliveriesPermission,
  DocumentsPermission,
  EntityVersionsPermission,
  FlowDefinitionsPermission,
  NegotiationsPermission,
  type Permission,
  PortalRequestsPermission,
  TemplatesPermission
} from './index'
import { MODULE_PERMISSIONS } from './modules'

export type PermissionGroupKey =
  | 'clients'
  | 'negotiations'
  | 'credits'
  | 'deliveries'
  | 'business'
  | 'inventory'
  | 'portal'
  | 'documents'

/**
 * Columna de la matriz donde vive el checkbox del permiso.
 * `action` = el grupo se renderiza con una única columna "PERMISO"
 * (permisos que no calzan en el CRUD clásico).
 */
export type PermissionColumn = 'read' | 'create' | 'update' | 'delete' | 'action'

export interface PermissionCatalogEntry {
  permission: Permission
  group: PermissionGroupKey
  /** Subheader dentro del tab (fila separadora). Sin sección = bloque inicial. */
  section?: string
  /** Etiqueta de la fila. Filas CRUD comparten label entre sus entradas. */
  label: string
  /** Texto de ayuda (HoverCard) */
  description?: string
  column: PermissionColumn
}

export interface PermissionGroup {
  key: PermissionGroupKey
  label: string
}

export const PERMISSION_GROUPS: readonly PermissionGroup[] = [
  { key: 'clients', label: 'Cliente' },
  { key: 'negotiations', label: 'Negociaciones' },
  // Entre Negociaciones y Postventa porque ahí está en el proceso real: el trámite de crédito es
  // lo que pasa ENTRE que la venta cierra y el inmueble se entrega. El orden de esta lista es el
  // orden de las pestañas.
  { key: 'credits', label: 'Créditos' },
  { key: 'deliveries', label: 'Postventa' },
  { key: 'business', label: 'Empresa' },
  { key: 'inventory', label: 'Inventario' },
  { key: 'portal', label: 'Portal de clientes' },
  { key: 'documents', label: 'Documentos del cliente' }
] as const

/** Helper para las filas CRUD del tab Empresa */
const crudRow = (
  moduleKey: keyof typeof MODULE_PERMISSIONS,
  label: string
): PermissionCatalogEntry[] => {
  const mod = MODULE_PERMISSIONS[moduleKey]
  const columns: PermissionColumn[] = ['read', 'create', 'update', 'delete']
  return columns
    .filter((column) => column in mod)
    .map((column) => ({
      permission: mod[column as keyof typeof mod] as Permission,
      group: 'business' as const,
      label,
      column
    }))
}

export const PERMISSION_CATALOG: readonly PermissionCatalogEntry[] = [
  // ── Cliente ────────────────────────────────────────────────────────────
  {
    permission: ClientsPermission.CreateClient,
    group: 'clients',
    label: 'Crear cliente',
    column: 'create'
  },
  {
    permission: ClientsPermission.ReadAllClients,
    group: 'clients',
    label: 'Consultar todos',
    description:
      'Si se selecciona esta opción, el usuario podra consultar todos los clientes, independientemente de que no los haya creado.',
    column: 'read'
  },
  {
    permission: ClientsPermission.UpdateClient,
    group: 'clients',
    label: 'Actualizar cliente',
    column: 'update'
  },
  {
    permission: ClientsPermission.DeleteClient,
    group: 'clients',
    label: 'Eliminar cliente',
    description:
      'Elimina el expediente completo (borrado lógico) y libera su DPI/NIT para volver a registrarlo. Solo aplica a clientes sin ninguna negociación. No disponible en producción.',
    column: 'delete'
  },
  {
    permission: ClientsPermission.DeleteFolderAndAttachmentsClient,
    group: 'clients',
    label: 'Eliminar carpetas y adjuntos',
    column: 'delete'
  },
  {
    permission: ClientsPermission.UpdateAdvisorClient,
    group: 'clients',
    label: 'Modificar asesor',
    column: 'update'
  },
  {
    permission: ClientsPermission.FieldRiskLevelClient,
    group: 'clients',
    section: 'Formularios de cliente',
    label: 'Campo de nivel de riesgo',
    description: 'Permite al usuario ver y editar el nivel de riesgo LD y FT de los clientes.',
    column: 'update'
  },

  // ── Negociaciones ──────────────────────────────────────────────────────
  {
    permission: NegotiationsPermission.CreateNegotiation,
    group: 'negotiations',
    label: 'Crear negociación',
    column: 'create'
  },
  {
    permission: NegotiationsPermission.ReadAllNegotiations,
    group: 'negotiations',
    label: 'Consultar todos',
    description:
      'Si se selecciona esta opción, el usuario podra consultar todos las negociaciones, independientemente de que no las haya creado.',
    column: 'read'
  },
  {
    permission: NegotiationsPermission.UpdateNegotiation,
    group: 'negotiations',
    label: 'Actualizar negociación',
    column: 'update'
  },
  {
    permission: NegotiationsPermission.DeleteFolderAndAttachmentsNegotiation,
    group: 'negotiations',
    label: 'Eliminar carpetas y adjuntos',
    column: 'delete'
  },
  {
    permission: NegotiationsPermission.UpdateAdvisorNegotiation,
    group: 'negotiations',
    label: 'Modificar asesor',
    column: 'update'
  },
  {
    permission: NegotiationsPermission.ActivateNegotiation,
    group: 'negotiations',
    label: 'Activar negociación',
    column: 'update'
  },
  {
    permission: NegotiationsPermission.RescindApprovedNegotiation,
    group: 'negotiations',
    label: 'Rescindir negociación aprobada',
    description:
      'Si se selecciona esta opción, el usuario podrá rescindir negociaciones que ya se encuentren aprobadas (estado finalizado). Este permiso solo habilita el botón cuando la negociación está aprobada y es independiente: no es necesario marcar también el permiso de rescindir del estado, basta con este. Si además se necesita rescindir en otros estados (por ejemplo, en proceso), se debe marcar adicionalmente el permiso de rescindir del estado correspondiente, ya que por seguridad son permisos distintos.',
    column: 'update'
  },
  {
    permission: NegotiationsPermission.FieldUbicationNegotiation,
    group: 'negotiations',
    section: 'Formulario Detalle de Negociación',
    label: 'Campo de ubicación de negociación',
    column: 'update'
  },
  {
    permission: NegotiationsPermission.FieldRiskLevelNegotiation,
    group: 'negotiations',
    section: 'Formulario Detalle de Negociación',
    label: 'Campo de nivel de riesgo de negociación',
    column: 'update'
  },
  {
    permission: NegotiationsPermission.FieldAmountsWithoutTaxNegotiation,
    group: 'negotiations',
    section: 'Formulario Detalle de Negociación',
    label: 'Valores sin impuestos',
    description:
      'Permite al usuario ver y editar los valores sin impuestos del acuerdo de pago: Valor del título, Valor total, Valor de Modificaciones (Upgrades) y Valor del inmueble.',
    column: 'update'
  },
  {
    permission: NegotiationsPermission.PrintFEIC,
    group: 'negotiations',
    section: 'Impresión de Documentos y Contratos',
    label: 'Imprimir documento FEIC',
    column: 'read'
  },
  {
    permission: NegotiationsPermission.PrintEquityStatement,
    group: 'negotiations',
    section: 'Impresión de Documentos y Contratos',
    label: 'Imprimir estado patrimonial',
    column: 'read'
  },
  {
    permission: NegotiationsPermission.PrintExtendedDueDiligence,
    group: 'negotiations',
    section: 'Impresión de Documentos y Contratos',
    label: 'Imprimir ampliación de debida diligencia',
    column: 'read'
  },
  {
    permission: NegotiationsPermission.PrintProductionOrder,
    group: 'negotiations',
    section: 'Impresión de Documentos y Contratos',
    label: 'Imprimir orden de producción',
    column: 'read'
  },
  {
    permission: NegotiationsPermission.PrintPaymentAgreement,
    group: 'negotiations',
    section: 'Impresión de Documentos y Contratos',
    label: 'Imprimir acuerdo de pago',
    column: 'read'
  },
  {
    permission: NegotiationsPermission.PrintPromise,
    group: 'negotiations',
    section: 'Impresión de Documentos y Contratos',
    label: 'Imprimir contrato de promesa',
    column: 'read'
  },
  {
    permission: NegotiationsPermission.PrintReferralNegotiation,
    group: 'negotiations',
    section: 'Impresión de Documentos y Contratos',
    label: 'Imprimir promociones',
    column: 'read'
  },
  {
    permission: NegotiationsPermission.PrintSpecialGuidelines,
    group: 'negotiations',
    section: 'Impresión de Documentos y Contratos',
    label: 'Imprimir lineamientos especiales',
    column: 'read'
  },
  {
    permission: NegotiationsPermission.PrintSellClosure,
    group: 'negotiations',
    section: 'Impresión de Documentos y Contratos',
    label: 'Imprimir cierre de venta',
    column: 'read'
  },
  {
    permission: NegotiationsPermission.PrintPurchaseAgreement,
    group: 'negotiations',
    section: 'Impresión de Documentos y Contratos',
    label: 'Imprimir acuerdo de compra',
    column: 'read'
  },
  {
    permission: NegotiationsPermission.PrintQuoteMaintenance,
    group: 'negotiations',
    section: 'Impresión de Documentos y Contratos',
    label: 'Imprimir alcance de cuota de mantenimiento',
    column: 'read'
  },
  {
    permission: NegotiationsPermission.ExportJSONFEIC,
    group: 'negotiations',
    section: 'Impresión de Documentos y Contratos',
    label: 'Exportar JSON FEIC',
    description:
      'Permite al usuario exportar información de FEIC en formato JSON para integración con otros sistemas.',
    column: 'read'
  },

  // ── Créditos (columna única) ───────────────────────────────────────────
  //
  // Todos leen, administración opera (R6). Por eso las dos primeras casillas se reparten ancho y
  // las demás no: el vendedor pregunta «¿en qué va el crédito?» todos los días, pero el trámite lo
  // lleva una sola área.
  //
  // ⚠️ Las dos de consulta son un PAR y el orden en el que están escritas es el orden en el que se
  // conceden: «Consultar» abre el módulo, «Consultar todos» ensancha el alcance. Tildar sólo la
  // segunda deja a alguien viendo el tablero entero sin la casilla base, que funciona igual —las
  // dos abren la pantalla— pero se lee al revés de como se reparte.
  {
    permission: CreditsPermission.ReadCredits,
    group: 'credits',
    label: 'Consultar los míos',
    description:
      'Permite ver el módulo de créditos y los expedientes de tu cartera: las negociaciones donde eres el asesor y las que creaste. Esta opción es necesaria para ver el módulo en el menú. Es solo lectura: no marca la checklist ni avanza etapas.',
    column: 'action'
  },
  {
    permission: CreditsPermission.ReadAllCredits,
    group: 'credits',
    label: 'Consultar todos',
    description:
      'Si se selecciona esta opción, el usuario podrá consultar todos los trámites de crédito, independientemente de que no sea el asesor de la negociación. Es solo lectura: no marca la checklist ni avanza etapas.',
    column: 'action'
  },
  {
    permission: CreditsPermission.StartCreditProcess,
    group: 'credits',
    label: 'Iniciar el trámite',
    description:
      'Permite abrir el expediente de crédito de una negociación cerrada sin esperar a que se abra solo.',
    column: 'action'
  },
  {
    permission: CreditsPermission.UpdateCreditChecklist,
    group: 'credits',
    label: 'Marcar la checklist',
    description:
      'Permite marcar y desmarcar los pasos del trámite y responder los datos que piden (banco, montos, fechas). Cada marca queda registrada con quién la hizo y cuándo.',
    column: 'action'
  },
  {
    permission: CreditsPermission.AdvanceCreditStage,
    group: 'credits',
    label: 'Avanzar de etapa',
    description:
      'Permite dar por cerrada una etapa y abrir la siguiente. Va aparte de marcar la checklist porque el proceso avisa lo que falta pero no frena: se puede avanzar dejando pasos sin marcar.',
    column: 'action'
  },
  {
    permission: CreditsPermission.ManageCreditDocuments,
    group: 'credits',
    label: 'Administrar los documentos del trámite',
    description:
      'Permite subir y quitar los archivos que piden los pasos: carta de autorización, solvencia, factura y demás.',
    column: 'action'
  },
  // ── Postventa ──────────────────────────────────────────────────────────
  {
    permission: DeliveriesPermission.ReadAllDeliveries,
    group: 'deliveries',
    label: 'Consultar todos',
    description:
      'Si se selecciona esta opción, el usuario podrá consultar todas las postventas. Esta opción es necesaria para ver el módulo en el menú.',
    column: 'read'
  },
  {
    permission: DeliveriesPermission.CreateDelivery,
    group: 'deliveries',
    label: 'Crear postventa',
    column: 'create'
  },
  {
    permission: DeliveriesPermission.UpdateDelivery,
    group: 'deliveries',
    label: 'Actualizar postventa',
    column: 'update'
  },
  {
    permission: DeliveriesPermission.MarkDeliveryAsCompleted,
    group: 'deliveries',
    label: 'Marcar postventa como completada',
    description: 'Permite al usuario marcar una postventa como completada.',
    column: 'update'
  },
  {
    permission: DeliveriesPermission.MarkDeliveryAsPending,
    group: 'deliveries',
    label: 'Marcar postventa como pendiente',
    description: 'Permite al usuario marcar una postventa como pendiente.',
    column: 'update'
  },

  // ── Empresa (filas CRUD) ───────────────────────────────────────────────
  ...crudRow('users', 'Usuarios'),
  ...crudRow('roles', 'Roles'),
  ...crudRow('companies', 'Empresas'),
  ...crudRow('agencies', 'Agencias'),
  ...crudRow('societies', 'Sociedades'),
  ...crudRow('projects', 'Proyectos'),
  ...crudRow('typeProperties', 'Tipos de propiedades'),
  ...crudRow('properties', 'Inmuebles'),
  ...crudRow('representants', 'Representantes'),
  ...crudRow('states', 'Estados'),
  ...crudRow('workflows', 'Flujos de trabajo'),
  ...crudRow('professions', 'Profesiones'),
  /**
   * ⚠️ VIVE EN EMPRESA, NO EN CRÉDITOS, y va DESPUÉS de Profesiones.
   *
   * Los otros cinco permisos de Créditos leen u operan un expediente; éste cambia las reglas con
   * las que van a nacer TODOS los expedientes futuros. Es configuración de la empresa, de la misma
   * naturaleza que Estados, Flujos de trabajo o Configuración de Clientes — y por eso se reparte a
   * otra gente: la semilla reparte los cinco del expediente y deja este SEXTO sin asignar.
   *
   * Va DESPUÉS de Profesiones, igual que el ítem del menú (`menu-business.tsx`), para que quien
   * busca la pantalla y quien busca su permiso lo encuentren en el mismo tramo de la lista. El
   * subnav interno y el menú lateral respetan el mismo orden: Profesiones, Créditos y Clientes.
   *
   * `column: 'action'` y no una fila CRUD porque es UNA capacidad indivisible: la misma llave abre
   * el editor y publica. Partirla en «consultar» y «actualizar» inventaría un permiso de lectura
   * que ninguna ruta pide. El grupo queda MIXTO y la matriz lo soporta (ver `resolveColumns`).
   */
  {
    permission: CreditsPermission.ManageCreditTemplates,
    group: 'business',
    label: 'Configuración de créditos',
    description:
      'Permite cambiar las etapas, los pasos y los datos que pide el proceso de crédito, y publicar una versión nueva. No toca los expedientes en curso: cada trámite se queda con las reglas que tenía cuando se abrió.',
    column: 'action'
  },
  {
    permission: ClientConfigPermission.ReadClientConfig,
    group: 'business',
    label: 'Configuración de Clientes',
    column: 'read'
  },
  {
    permission: ClientConfigPermission.UpdateClientConfig,
    group: 'business',
    label: 'Configuración de Clientes',
    column: 'update'
  },
  // Las plantillas del editor de documentos (Documentos de tipo de propiedad y de
  // proyecto). Sin estas filas, el botón «Guardar» del editor sólo lo veía una
  // cuenta admin: el permiso existía pero no había dónde tildarlo.
  ...(
    [
      [TemplatesPermission.ReadTemplate, 'read'],
      [TemplatesPermission.CreateTemplate, 'create'],
      [TemplatesPermission.UpdateTemplate, 'update'],
      [TemplatesPermission.DeleteTemplate, 'delete']
    ] as const
  ).map(
    ([permission, column]): PermissionCatalogEntry => ({
      permission,
      group: 'business',
      label: 'Plantillas de documentos',
      column
    })
  ),

  // ── Documentos del cliente (columna única) ─────────────────────────────
  // Ninguno cuelga del CRUD de catálogo (ADR 0023): nadie tiene `UpdateSocieties`
  // y de los cinco equipos del cartapacio sólo uno tiene `UpdateProjects`.
  {
    permission: DocumentsPermission.ManageCatalogDocuments,
    group: 'documents',
    label: 'Administrar documentos de catálogo',
    description:
      'Permite subir y organizar documentos en sociedades, proyectos, tipos de inmueble e inmuebles. No los publica.',
    column: 'action'
  },
  {
    permission: DocumentsPermission.PublishCatalogDocuments,
    group: 'documents',
    label: 'Publicar documentos de catálogo',
    description:
      'Permite hacer visible un documento para los clientes. Publicar en un proyecto le llega a TODOS sus clientes.',
    column: 'action'
  },
  {
    permission: DocumentsPermission.ManageNegotiationDocuments,
    group: 'documents',
    label: 'Administrar documentos del expediente',
    description:
      'Permite curar el árbol de una negociación —incluso después de aprobada— sin poder modificar la negociación.',
    column: 'action'
  },

  // ── Inventario (columna única) ─────────────────────────────────────────
  {
    permission: AvailabilityPermission.ReadAvailability,
    group: 'inventory',
    label: 'Consultar disponibilidad',
    column: 'action'
  },
  {
    permission: AvailabilityPermission.CreateReservation,
    group: 'inventory',
    label: 'Crear apartado',
    column: 'action'
  },
  {
    permission: AvailabilityPermission.ManageReservations,
    group: 'inventory',
    label: 'Administrar apartados',
    description:
      'Permite liberar apartados de otros usuarios y gestionar todos los apartados del sistema.',
    column: 'action'
  },
  {
    permission: AvailabilityPermission.ReadReservationHistory,
    group: 'inventory',
    label: 'Ver historial de apartados',
    column: 'action'
  },
  {
    permission: AvailabilityPermission.ReadInventoryComments,
    group: 'inventory',
    label: 'Ver comentarios de inmuebles',
    column: 'action'
  },
  {
    permission: AvailabilityPermission.CreateInventoryComment,
    group: 'inventory',
    label: 'Comentar inmuebles',
    column: 'action'
  },
  {
    permission: AvailabilityPermission.ManageInventoryComments,
    group: 'inventory',
    label: 'Administrar comentarios',
    description:
      'Permite eliminar comentarios de cualquier usuario (moderación). Sin este permiso, cada quien solo puede eliminar los suyos.',
    column: 'action'
  },

  // ── Portal de clientes (columna única) ─────────────────────────────────
  {
    permission: PortalRequestsPermission.CreatePortalRequest,
    group: 'portal',
    section: 'Solicitudes de verificación',
    label: 'Crear y enviar solicitud',
    description:
      'Permite crear una solicitud de verificación y enviar el enlace del formulario al cliente. Incluye el alta rápida de clientes y las revalidaciones manuales.',
    column: 'action'
  },
  {
    permission: PortalRequestsPermission.ReadPortalRequests,
    group: 'portal',
    section: 'Solicitudes de verificación',
    label: 'Consultar solicitudes',
    description:
      'Permite ver la lista de solicitudes, el detalle del expediente y las métricas de revalidación.',
    column: 'action'
  },
  {
    permission: PortalRequestsPermission.ReviewPortalRequest,
    group: 'portal',
    section: 'Solicitudes de verificación',
    label: 'Revisar expediente',
    description:
      'Permite marcar campos como correctos o con corrección, traer el formulario a revisión, solicitar correcciones al cliente, editar campos del solicitante y administrar los documentos del expediente.',
    column: 'action'
  },
  {
    permission: PortalRequestsPermission.ApprovePortalRequest,
    group: 'portal',
    section: 'Solicitudes de verificación',
    label: 'Aprobar y sincronizar',
    description:
      'Permite aprobar el expediente y sincronizar la información verificada al registro interno del cliente.',
    column: 'action'
  },
  {
    permission: PortalRequestsPermission.RejectPortalRequest,
    group: 'portal',
    section: 'Solicitudes de verificación',
    label: 'Rechazar expediente',
    description: 'Permite rechazar el expediente completo y notificar al cliente.',
    column: 'action'
  },
  {
    permission: PortalRequestsPermission.RevokePortalRequest,
    group: 'portal',
    section: 'Solicitudes de verificación',
    label: 'Cancelar flujo',
    description: 'Permite cancelar el flujo y revocar el enlace enviado al cliente.',
    column: 'action'
  },
  {
    permission: PortalRequestsPermission.ExtendPortalLink,
    group: 'portal',
    section: 'Solicitudes de verificación',
    label: 'Renovar enlace vencido',
    description:
      'Permite extender la expiración de un enlace vencido para que el cliente retome el formulario.',
    column: 'action'
  },
  {
    permission: PortalRequestsPermission.RevealPortalLink,
    group: 'portal',
    section: 'Solicitudes de verificación',
    label: 'Ver y rotar el enlace',
    description:
      'Permite ver en pantalla el enlace del formulario para compartirlo por fuera del correo, y rotarlo para invalidar el anterior. El enlace abre el expediente del cliente sin pedir contraseña: cada vez que alguien lo ve queda registrado en la bitácora.',
    column: 'action'
  },
  {
    permission: PortalRequestsPermission.DeleteOthersCorrections,
    group: 'portal',
    section: 'Solicitudes de verificación',
    label: 'Eliminar correcciones de otros',
    description:
      'Permite eliminar comentarios/correcciones creados por otros revisores. Las correcciones propias siempre se pueden eliminar sin este permiso.',
    column: 'action'
  },
  {
    permission: FlowDefinitionsPermission.ReadFlowDefinitions,
    group: 'portal',
    section: 'Definiciones de flujo',
    label: 'Consultar definiciones de flujo',
    column: 'action'
  },
  {
    permission: FlowDefinitionsPermission.CreateFlowDefinitions,
    group: 'portal',
    section: 'Definiciones de flujo',
    label: 'Crear definiciones de flujo',
    column: 'action'
  },
  {
    permission: FlowDefinitionsPermission.UpdateFlowDefinitions,
    group: 'portal',
    section: 'Definiciones de flujo',
    label: 'Editar definiciones de flujo',
    column: 'action'
  },
  {
    permission: FlowDefinitionsPermission.DeactivateFlowDefinitions,
    group: 'portal',
    section: 'Definiciones de flujo',
    label: 'Desactivar definiciones de flujo',
    description:
      'La definición no se elimina: queda inactiva y deja de ofrecerse al crear solicitudes.',
    column: 'action'
  },
  // Historial de versiones (ADR 0029). Nacen sin asignar a ningún rol: hasta que alguien los
  // conceda acá, el historial no lo ve nadie.
  //
  // Viven en la pestaña de SU dominio y no en «Empresa»: quien reparte permisos de clientes
  // decide sobre el historial de clientes, en la misma pantalla donde ya está decidiendo quién
  // los ve y quién los edita. Tenerlos juntos en un cajón aparte invitaba a concederlos de a
  // dos sin mirar de qué expediente hablaba cada uno.
  {
    permission: EntityVersionsPermission.ReadClientVersions,
    group: 'clients',
    section: 'Historial de versiones',
    label: 'Consultar el historial de un cliente',
    description:
      'Permite ver cómo estaba un cliente antes de cada edición, y quién la hizo. Va aparte de poder ver el cliente: el historial guarda estados anteriores, incluidos datos que alguien corrigió justamente porque no correspondía que estuvieran —un documento de identidad equivocado, un patrimonio mal cargado— y acá siguen visibles. Sigue respetando la cartera: un asesor sólo ve el historial de los clientes que ya puede abrir.',
    column: 'action'
  },
  {
    permission: EntityVersionsPermission.RestoreClientVersion,
    group: 'clients',
    section: 'Historial de versiones',
    label: 'Restaurar una versión anterior del cliente',
    description:
      'Permite devolver un cliente a como estaba en una versión previa. Va aparte de poder editarlo: restaurar deshace el trabajo de otra persona. No recupera archivos borrados ni deshace correos ya enviados, y no se puede mientras el cliente tenga un formulario del portal abierto.',
    column: 'action'
  },
  // ── App móvil del cliente ──────────────────────────────────────────────────
  //
  // Sección dentro de «Clientes» y no una pestaña propia: son tres casillas, y un tab
  // para tres casillas es ruido. Quien reparte permisos de clientes es quien decide si
  // alguien puede abrirles acceso a la app.
  //
  // Las descripciones dicen la CONSECUENCIA, no la mecánica: dos de las tres acciones
  // terminan en el buzón o en el teléfono de una persona real.
  {
    permission: ClientAppPermission.ReadClientAppAccounts,
    group: 'clients',
    section: 'App móvil',
    label: 'Consultar las cuentas de la app',
    description:
      'Permite ver quién tiene acceso a la aplicación del cliente, desde qué dispositivos, y por qué alguien dejó de ver su inmueble. Es sólo lectura: no abre cuentas, no concede accesos y no bloquea. Sigue respetando la cartera: un asesor sólo ve las cuentas de los clientes que ya puede abrir.',
    column: 'action'
  },
  {
    permission: ClientAppPermission.ManageClientAppAccess,
    group: 'clients',
    section: 'App móvil',
    label: 'Administrar el acceso a la app',
    description:
      'Permite abrir la cuenta de un cliente, concederle y quitarle inmuebles, reenviarle la invitación, corregir su correo y cerrar sus sesiones. Va aparte de poder editar al cliente porque varias de estas acciones ENVÍAN UN CORREO a una persona real, y un correo enviado no se deshace.',
    column: 'action'
  },
  {
    permission: ClientAppPermission.BlockClientAppAccount,
    group: 'clients',
    section: 'App móvil',
    label: 'Bloquear y reactivar una cuenta de la app',
    description:
      'Permite dejar a un cliente fuera de la aplicación al instante, cerrando todas sus sesiones. Va aparte de administrar el acceso porque es una decisión de seguridad: mientras la cuenta esté bloqueada, aprobar una venta NO le devuelve el acceso; alguien tiene que reactivarla a mano.',
    column: 'action'
  },

  {
    permission: EntityVersionsPermission.ReadNegotiationVersions,
    group: 'negotiations',
    section: 'Historial de versiones',
    label: 'Consultar el historial de una negociación',
    description:
      'Permite ver cómo estaba una negociación antes de cada edición, y quién la hizo. Va aparte de poder ver la negociación: el historial guarda estados anteriores, incluidos datos que alguien corrigió porque estaban mal. Sigue respetando la cartera: un asesor sólo ve el historial de las negociaciones que ya puede abrir.',
    column: 'action'
  },
  {
    permission: EntityVersionsPermission.RestoreNegotiationVersion,
    group: 'negotiations',
    section: 'Historial de versiones',
    label: 'Restaurar una versión anterior de la negociación',
    description:
      'Permite devolver una negociación a como estaba en una versión previa. Va aparte de poder editarla: restaurar deshace el trabajo de otra persona. NO mueve el estado del flujo —para eso están las transiciones— ni deshace lo que ya ocurrió: correos enviados, órdenes despachadas a SAP o archivos eliminados siguen igual.',
    column: 'action'
  }
] as const

/** Entradas del catálogo agrupadas por tab, en el orden declarado */
export const getCatalogByGroup = (group: PermissionGroupKey): readonly PermissionCatalogEntry[] =>
  PERMISSION_CATALOG.filter((entry) => entry.group === group)
